import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { BLEEP_SERVER_IDS, BLEEP_SERVER_NAMES } from '../../utils/constants';
import { getOnboardingPurgatorySyncStatus } from '../../services/onboardingService';
import {
    FaServer,
    FaSync,
    FaCheckCircle,
    FaExclamationCircle,
    FaExclamationTriangle,
    FaLayerGroup,
    FaUserSlash,
    FaClock,
} from 'react-icons/fa';

const SYNC_STATUS_CONFIG = {
    completed: {
        label: 'Up to date',
        color: '#03543f',
        bg: '#def7ec',
        border: '#84e1bc',
        icon: FaCheckCircle,
    },
    completed_with_issues: {
        label: 'Review items',
        color: '#92400e',
        bg: '#fef3c7',
        border: '#fcd34d',
        icon: FaExclamationTriangle,
    },
    running: {
        label: 'Sync in progress',
        color: '#1e40af',
        bg: '#dbeafe',
        border: '#93c5fd',
        icon: FaSync,
        spin: true,
    },
    failed: {
        label: 'Sync failed',
        color: '#991b1b',
        bg: '#fee2e2',
        border: '#fca5a5',
        icon: FaExclamationCircle,
    },
    interrupted: {
        label: 'Sync interrupted',
        color: '#991b1b',
        bg: '#fee2e2',
        border: '#fca5a5',
        icon: FaExclamationCircle,
    },
};

const GlobalPurgatoryOverview = ({ selectedServer, onSelectServer }) => {
    const [metrics, setMetrics] = useState({});
    const [loading, setLoading] = useState(false);
    const [lastFetched, setLastFetched] = useState(null);

    const loadGlobalMetrics = useCallback(async () => {
        setLoading(true);
        const newMetrics = {};

        try {
            await Promise.all(
                BLEEP_SERVER_IDS.map(async (sid) => {
                    const serverMetric = {
                        pendingCount: 0,
                        syncStatus: 'idle',
                        inactivityDue: 0,
                    };

                    // 1. Pending items query
                    try {
                        const qPending = query(
                            collection(db, 'servers', sid, 'onboardingPurgatory'),
                            where('reviewStatus', '==', 'pending')
                        );
                        const snapPending = await getDocs(qPending);
                        serverMetric.pendingCount = snapPending.size;
                    } catch (err) {
                        console.error(`Failed to get pending count for ${sid}:`, err);
                    }

                    // 2. Sync health status
                    try {
                        const syncRes = await getOnboardingPurgatorySyncStatus(sid);
                        if (syncRes?.available && syncRes?.status) {
                            serverMetric.syncStatus = syncRes.isInterrupted ? 'interrupted' : syncRes.status;
                        } else {
                            serverMetric.syncStatus = 'idle';
                        }
                    } catch {
                        serverMetric.syncStatus = 'idle';
                    }

                    // 3. Inactivity due metrics
                    try {
                        const latestStateSnap = await getDoc(
                            doc(db, 'servers', sid, 'employeeMaintenanceState', 'latestCompleted')
                        );
                        if (latestStateSnap.exists()) {
                            const runID = latestStateSnap.data()?.runID;
                            if (runID) {
                                const runSnap = await getDoc(
                                    doc(db, 'servers', sid, 'employeeMaintenanceRuns', runID)
                                );
                                if (runSnap.exists()) {
                                    const outcomes = runSnap.data()?.summary?.outcomes || {};
                                    serverMetric.inactivityDue =
                                        (Number(outcomes.WOULD_TERMINATE) || 0) +
                                        (Number(outcomes.DUE_TO_TERMINATE) || 0);
                                }
                            }
                        }
                    } catch {
                        serverMetric.inactivityDue = 0;
                    }

                    newMetrics[sid] = serverMetric;
                })
            );

            setMetrics(newMetrics);
            setLastFetched(new Date());
        } catch (globalErr) {
            console.error('Failed to load global overview metrics:', globalErr);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGlobalMetrics();
    }, [loadGlobalMetrics]);

    const totalPending = Object.values(metrics).reduce(
        (acc, m) => acc + (m?.pendingCount || 0),
        0
    );

    const totalInactivityDue = Object.values(metrics).reduce(
        (acc, m) => acc + (m?.inactivityDue || 0),
        0
    );

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
            {/* Overview Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '14px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaLayerGroup style={{ color: '#2b6cb0', fontSize: '18px' }} />
                    <div>
                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#2d3748' }}>
                            Super Admin Global Overview & Franchise Health
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096' }}>
                            Live queues & care metrics across all 4 servers · Total pending:{' '}
                            <strong style={{ color: totalPending > 0 ? '#b45309' : '#276749' }}>
                                {totalPending}
                            </strong>
                            {' · '}
                            Inactivity due:{' '}
                            <strong style={{ color: totalInactivityDue > 0 ? '#c53030' : '#4a5568' }}>
                                {totalInactivityDue}
                            </strong>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {lastFetched && (
                        <span style={{ fontSize: '11px', color: '#a0aec0' }}>
                            Updated {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={loadGlobalMetrics}
                        disabled={loading}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#f8fafc',
                            color: '#2b6cb0',
                            border: '1px solid #cbd5e0',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <FaSync className={loading ? 'fa-spin' : ''} style={{ fontSize: '11px' }} />
                        <span>Refresh Metrics</span>
                    </button>
                </div>
            </div>

            {/* 4-Server Grid Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '12px',
            }}>
                {BLEEP_SERVER_IDS.map((sid) => {
                    const isSelected = selectedServer === sid;
                    const serverMetric = metrics[sid] || { pendingCount: 0, syncStatus: 'idle', inactivityDue: 0 };
                    const pendingCount = serverMetric.pendingCount;
                    const hasPending = pendingCount > 0;
                    const inactivityDue = serverMetric.inactivityDue;
                    const hasInactivity = inactivityDue > 0;

                    const syncCfg = SYNC_STATUS_CONFIG[serverMetric.syncStatus] || {
                        label: 'Awaiting sync',
                        color: '#4b5563',
                        bg: '#f3f4f6',
                        border: '#d1d5db',
                        icon: FaClock,
                    };
                    const SyncIcon = syncCfg.icon;

                    return (
                        <div
                            key={sid}
                            onClick={() => onSelectServer && onSelectServer(sid)}
                            style={{
                                padding: '14px 16px',
                                borderRadius: '10px',
                                border: isSelected ? '2px solid #2b6cb0' : '1px solid #e2e8f0',
                                backgroundColor: isSelected ? '#ebf8ff' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease-in-out',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                boxShadow: isSelected ? '0 2px 4px rgba(43,108,176,0.1)' : '0 1px 2px rgba(0,0,0,0.03)',
                            }}
                        >
                            {/* Card Top: Server Info & Selection Indicator */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <div style={{
                                        fontWeight: '700',
                                        fontSize: '13.5px',
                                        color: isSelected ? '#2b6cb0' : '#183D44',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}>
                                        <FaServer style={{ fontSize: '11px', color: isSelected ? '#2b6cb0' : '#718096' }} />
                                        <span>{BLEEP_SERVER_NAMES[sid] || sid}</span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#718096', marginTop: '1px', fontFamily: 'monospace' }}>
                                        {sid}
                                    </div>
                                </div>

                                {isSelected && (
                                    <span style={{
                                        fontSize: '10.5px',
                                        fontWeight: '700',
                                        color: '#2b6cb0',
                                        backgroundColor: '#bee3f8',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                    }}>
                                        Active
                                    </span>
                                )}
                            </div>

                            {/* Card Metrics Grid (3 Pillars: Pending Reviews, Sync Health, Inactivity Due) */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '6px',
                                backgroundColor: isSelected ? '#ffffff' : '#f8fafc',
                                borderRadius: '8px',
                                padding: '8px',
                                border: '1px solid #edf2f7',
                            }}>
                                {/* Pillar 1: Pending Items */}
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '10.5px', color: '#718096', fontWeight: '600', marginBottom: '2px' }}>
                                        Pending
                                    </div>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        padding: '2px 6px',
                                        borderRadius: '10px',
                                        fontSize: '11.5px',
                                        fontWeight: '700',
                                        backgroundColor: hasPending ? '#fffaf0' : '#f0fff4',
                                        color: hasPending ? '#b45309' : '#276749',
                                        border: `1px solid ${hasPending ? '#fbd38d' : '#c6f6d5'}`,
                                    }}>
                                        {hasPending ? (
                                            <FaExclamationCircle style={{ fontSize: '9px' }} />
                                        ) : (
                                            <FaCheckCircle style={{ fontSize: '9px' }} />
                                        )}
                                        <span>{pendingCount}</span>
                                    </span>
                                </div>

                                {/* Pillar 2: Sync Health */}
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '10.5px', color: '#718096', fontWeight: '600', marginBottom: '2px' }}>
                                        Sync
                                    </div>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        padding: '2px 6px',
                                        borderRadius: '10px',
                                        fontSize: '10.5px',
                                        fontWeight: '700',
                                        backgroundColor: syncCfg.bg,
                                        color: syncCfg.color,
                                        border: `1px solid ${syncCfg.border}`,
                                        whiteSpace: 'nowrap',
                                    }}>
                                        <SyncIcon className={syncCfg.spin ? 'fa-spin' : ''} style={{ fontSize: '8px' }} />
                                        <span>{syncCfg.label}</span>
                                    </span>
                                </div>

                                {/* Pillar 3: Inactivity Due */}
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '10.5px', color: '#718096', fontWeight: '600', marginBottom: '2px' }}>
                                        Inactive Due
                                    </div>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        padding: '2px 6px',
                                        borderRadius: '10px',
                                        fontSize: '11.5px',
                                        fontWeight: '700',
                                        backgroundColor: hasInactivity ? '#fff5f5' : '#f7fafc',
                                        color: hasInactivity ? '#c53030' : '#718096',
                                        border: `1px solid ${hasInactivity ? '#fed7d7' : '#e2e8f0'}`,
                                    }}>
                                        {hasInactivity && <FaUserSlash style={{ fontSize: '9px' }} />}
                                        <span>{inactivityDue}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GlobalPurgatoryOverview;
